import React, { memo, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { formats, validators, arrays } from 'investira.sdk';
import {
  Stack,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  DatePicker,
  Divider,
  Typography,
  Skeleton,
  Card
} from 'investira.react.components';
import { COTACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import { VALIDATIONS } from '../../../../../const';
import {
  INDICADOR_DISPLAY_MODE,
  getIndicadorDisplayConfig
} from '../../../../../components/organisms/indicadorDisplayMode';
import {
  formatIndicadorDisplayValue,
  getIndicadorCotacaoResumo,
  getIndicadorDisplayValue
} from '../_shared/indicadoresCotacao.helpers';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
const IndicadoresTabular = memo(props => {
  const {
    dataInicio,
    dataFim,
    indicador,
    cotacoes,
    isLoading = false,
    displayMode = INDICADOR_DISPLAY_MODE.outros
  } = props;
  const xDisplayConfig = getIndicadorDisplayConfig(displayMode);
  const xCotacaoTipo = arrays.arrayToObject(Object.values(COTACAO_TIPO), 'id');
  const { divulgacao = {} } = indicador;
  const xIntervaloDisponivel = useMemo(() => {
    const xDatas = (cotacoes?.[0]?.data || [])
      .map(pCotacao => dayjs(pCotacao.data))
      .filter(pData => pData.isValid())
      .sort((pPrimeiraData, pSegundaData) => pPrimeiraData.valueOf() - pSegundaData.valueOf());
    const xDataMinima = xDatas[0] || dayjs(dataInicio);
    const xDataMaxima = xDatas[xDatas.length - 1] || dayjs(dataFim);
    const xDataInicioPadrao = xDataMaxima.subtract(6, 'month');

    return {
      dataMinima: xDataMinima,
      dataMaxima: xDataMaxima,
      dataInicio: xDataInicioPadrao.isBefore(xDataMinima, 'day') ? xDataMinima : xDataInicioPadrao
    };
  }, [cotacoes, dataFim, dataInicio]);
  const VALIDATION_SCHEMA = Yup.object().shape({
    data_inicio: Yup.date().max(new Date()).required(VALIDATIONS.REQUIRED),
    data_fim: Yup.date().max(new Date()).required(VALIDATIONS.REQUIRED)
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      data_inicio: xIntervaloDisponivel.dataInicio,
      data_fim: xIntervaloDisponivel.dataMaxima
    },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: false,
    onSubmit: (pValues, pActions) => {
      pActions.setSubmitting(true);
    }
  });
  const handleChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };
  const filtrarPorData = (pDados, pDataInicio, pDataFim) => {
    return pDados.filter(
      xItem =>
        dayjs(xItem.data).isSameOrAfter(pDataInicio, 'day') &&
        dayjs(xItem.data).isSameOrBefore(pDataFim, 'day')
    );
  };
  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height={360} />;
  }
  if (validators.isEmpty(cotacoes[0].data)) {
    return null;
  }
  const xCotacoesFiltered = filtrarPorData(
    cotacoes[0].data,
    formik.values.data_inicio,
    formik.values.data_fim
  );
  const xResumoPeriodo =
    xCotacoesFiltered.length >= 2
      ? getIndicadorCotacaoResumo(xCotacoesFiltered, xDisplayConfig)
      : null;
  const xVariacaoPeriodo = xResumoPeriodo?.variacaoInicio;
  return (
    <Card>
      <Stack sx={{ py: 2 }}>
        <Stack
          sx={{
            gap: '16px 16px',
            px: 2,
            py: 2
          }}
          flexWrap="wrap"
          direction="row">
          <Stack
            sx={{
              flexGrow: 1
            }}>
            <Typography variant="caption" color="textSecondary">
              {xDisplayConfig.variationLabel} no período
            </Typography>
            <Typography variant="h6" color="textPrimary">
              {formatIndicadorDisplayValue(xVariacaoPeriodo, xDisplayConfig, true)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 2
            }}>
            <DatePicker
              label="Data Início"
              name="data_inicio"
              onChange={pValue => {
                handleChange('data_inicio', pValue);
              }}
              value={formik.values.data_inicio}
              error={Boolean(formik.errors.data_inicio)}
              helperText={formik.errors.data_inicio}
              disabled={formik.isSubmitting}
              minDate={xIntervaloDisponivel.dataMinima}
              maxDate={formik.values.data_fim}
              fullWidth
            />
            <DatePicker
              label="Data Fim"
              name="data_fim"
              onChange={pValue => {
                handleChange('data_fim', pValue);
              }}
              value={formik.values.data_fim}
              error={Boolean(formik.errors.data_fim)}
              helperText={formik.errors.data_fim}
              disabled={formik.isSubmitting}
              minDate={formik.values.data_inicio}
              maxDate={xIntervaloDisponivel.dataMaxima}
              fullWidth
            />
          </Stack>
        </Stack>
        <Divider />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 480, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: '25%',
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: pTheme => pTheme.palette.secondary.dark
                  }}>
                  Data
                </TableCell>
                {/* <TableCell>Data Divulgação</TableCell> */}
                <TableCell align="right" sx={{ width: '30%', whiteSpace: 'nowrap' }}>
                  {xDisplayConfig.valueLabel}
                </TableCell>
                <TableCell align="center" sx={{ width: '45%', whiteSpace: 'nowrap' }}>
                  Cotação Tipo
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...xCotacoesFiltered].reverse().map(row => (
                <TableRow
                  key={`${row.data}-${row.cotacao_tipo_id}`}
                  sx={{
                    '&:last-child td, &:last-child th': {
                      border: 0
                    }
                  }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: pTheme => pTheme.palette.secondary.dark
                    }}>
                    {divulgacao?.periodicidade === 'M'
                      ? formats.formatDateCustom(row.data, 'MMM/YY')
                      : formats.formatDate(row.data)}
                  </TableCell>
                  {/* <TableCell>
                    {formats.formatDate(row.data_divulgacao)}
                   </TableCell> */}
                  <TableCell align="right">
                    {formatIndicadorDisplayValue(
                      getIndicadorDisplayValue(row, xDisplayConfig),
                      xDisplayConfig
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {xCotacaoTipo[row.cotacao_tipo_id]?.descricao}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Card>
  );
});
IndicadoresTabular.displayName = 'IndicadoresTabular';
export default IndicadoresTabular;
